import { useState } from 'react';
import { Plus, Settings, Archive, ArchiveRestore } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useLanguage } from '@/contexts/LanguageContext';
import { useQuestionnaires } from '@/hooks/use-questionnaires';
import { CreateQuestionnaireDialog } from './CreateQuestionnaireDialog';
import { EditQuestionnaireDialog } from './EditQuestionnaireDialog';
import { AssignmentRules } from './AssignmentRules';
import { VALIDITY_DAYS_DEFAULT } from '@/lib/assessment/namatAssessment';
import type { AssignmentRule } from '@/lib/assessment/questionnaireAssignment';
import type { Questionnaire } from '@/types/questionnaire';
import { format } from 'date-fns';

export function QuestionnaireManagement() {
  const { t, dir, language } = useLanguage();
  const { questionnaires, loading, saving, updateQuestionnaire } = useQuestionnaires();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingQuestionnaire, setEditingQuestionnaire] = useState<Questionnaire | null>(null);
  const [rules, setRules] = useState<AssignmentRule[]>([]);
  const Q = 'dashboard.admin.questionnaires';

  // Archived questionnaires are kept with their answers but never assigned (#92).
  const toggleArchived = async (questionnaire: Questionnaire) => {
    if (!questionnaire.archived && rules.some((r) => r.questionnaireId === questionnaire.id)) {
      toast.error(t(`${Q}.archiveInUse`));
      return;
    }
    await updateQuestionnaire(questionnaire.id, { archived: !questionnaire.archived });
  };

  // How long one fill stays valid before it has to be redone (#92).
  const saveValidity = async (questionnaire: Questionnaire, value: string) => {
    const days = Math.floor(Number(value));
    if (!Number.isFinite(days) || days < 1) return;
    if (days === (questionnaire.validityDays ?? VALIDITY_DAYS_DEFAULT)) return;
    await updateQuestionnaire(questionnaire.id, { validityDays: days });
    toast.success(t(`${Q}.validitySaved`));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('dashboard.admin.questionnaires.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={dir}>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-bold tracking-tight">
            {t('dashboard.admin.questionnaires.title')}
          </h2>
          <p className="text-muted-foreground">
            {t('dashboard.admin.questionnaires.description')}
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
          {t('dashboard.admin.questionnaires.createNew')}
        </Button>
      </div>

      <AssignmentRules questionnaires={questionnaires} onRulesChange={setRules} />

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {questionnaires.map((questionnaire) => (
          <Card key={questionnaire.id} className={`relative ${questionnaire.archived ? 'opacity-60' : ''}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg">
                    {language === 'ar' ? questionnaire.name.ar : questionnaire.name.en}
                  </CardTitle>
                  {questionnaire.description && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {language === 'ar' ? questionnaire.description.ar : questionnaire.description.en}
                    </p>
                  )}
                </div>
                {questionnaire.archived && (
                  <Badge variant="secondary" className="ms-2">
                    {t(`${Q}.archived`)}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-muted-foreground">
                <div>{questionnaire.questions.length} {t('dashboard.admin.questionnaires.questions')}</div>
                <div>{t('dashboard.admin.questionnaires.createdAt')}: {format(questionnaire.createdAt, 'PPP')}</div>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">{t(`${Q}.validity`)}</span>
                <Input
                  type="number"
                  min={1}
                  className="h-8 w-20"
                  defaultValue={questionnaire.validityDays ?? VALIDITY_DAYS_DEFAULT}
                  onBlur={(e) => saveValidity(questionnaire, e.target.value)}
                  disabled={saving}
                />
                <span className="text-muted-foreground">{t(`${Q}.days`)}</span>
              </label>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditingQuestionnaire(questionnaire)}
                  className="flex-1"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  {t('dashboard.admin.questionnaires.edit')}
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleArchived(questionnaire)}
                  disabled={saving}
                  aria-label={t(questionnaire.archived ? `${Q}.unarchive` : `${Q}.archive`)}
                  title={t(questionnaire.archived ? `${Q}.unarchive` : `${Q}.archive`)}
                >
                  {questionnaire.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {questionnaires.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="text-muted-foreground text-center">
              <p className="text-lg mb-2">{t('dashboard.admin.questionnaires.noQuestionnaires')}</p>
              <p className="text-sm">{t('dashboard.admin.questionnaires.createFirstQuestionnaire')}</p>
            </div>
            <Button onClick={() => setShowCreateDialog(true)} className="mt-4">
              <Plus className="h-4 w-4 mr-2" />
              {t('dashboard.admin.questionnaires.createNew')}
            </Button>
          </CardContent>
        </Card>
      )}

      <CreateQuestionnaireDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />

      {editingQuestionnaire && (
        <EditQuestionnaireDialog
          questionnaire={editingQuestionnaire}
          open={!!editingQuestionnaire}
          onOpenChange={(open) => !open && setEditingQuestionnaire(null)}
        />
      )}

    </div>
  );
}
